import React, { useState } from "react"
import { Tooltip } from "react-tooltip"
import WellbeingIndexChart from "../WellbeingIndexChart"
import WellbeingIndexChartLlsBioAge from "../WellbeingIndexChartLlsBioAge"
import IndexGaugeSmall from "../IndexGaugeSmall"
import IndexGaugeSmallLlsBioAge from "../IndexGaugeSmallLlsBioAge"
import WellbeingIndexChartTiny from "../WellbeingIndexChartTiny"
import SectionRetakeButton from "./SectionRetakeButton"
import { ageChangeScoreDisplay, pqIndexBandName } from "./constants"
import { getAssetPath } from "../../../utils/assets"

const SECTIONS = [
  {
    key: "energy_management",
    title: "Energy Management",
    icon: "PQ-energy-management",
    anchor: "energyManagementTrend",
    buildingLabel: "Optimise your PQ",
    description: (
      <>
        Energy Management is the deliberate practice of building and replenishing physical and
        cognitive energy across the day, the week, and the year — supported by a system that
        helps maintain the optimal balance between stress (fuel for growth) and recovery (rest
        and restoration). It is influenced by physical activity, nutrition, sleep and recovery.
      </>
    ),
    buildingContent: [
      {
        left: { heading: "Move consistently across the day.", text: "Stay active with regular walking and purposeful exercise. Frequent movement delivers compounding physical, cognitive, and emotional benefits over time." },
        right: { heading: "Prioritise aerobic fitness.", text: "Include regular low-to-moderate intensity (Zone 2) cardio to support fat metabolism, mitochondrial health, and long-term metabolic flexibility and fitness." }
      },
      {
        left: { heading: "Eat to support stable energy.", text: "Choose mostly fresh, minimally processed foods and eat regular, balanced meals to stabilise blood sugar and reduce reliance on caffeine or sugar for short-term energy." },
        right: { heading: "Protect sleep and daily recovery.", text: "Treat sleep and recovery as performance essentials, supported by consistent routines and brief recovery breaks built into each day." }
      },
      {
        left: { heading: "Align work with natural energy rhythms.", text: "Schedule demanding, high-value tasks during peak energy periods and lower-stakes work during natural dips to increase output without extending working hours." },
        right: { heading: "Manage what drains and energises you.", text: "Identify activities, commitments, and interactions that deplete energy alongside those that restore it, and manage the balance intentionally." }
      },
      {
        left: { heading: "Seek challenge and personal growth.", text: "Actively pursue new challenges and diverse experiences that stimulate continuous learning, meaningful engagement, and positive stress that fuels growth." },
        right: { heading: "Align action with meaning and purpose.", text: "Become conscious of what provides meaning in your life and align daily behaviours and decisions with your personal purpose." }
      }
    ]
  },
  {
    key: "strategic_recovery",
    title: "Strategic Recovery",
    icon: "PQ-strategic-recovery",
    anchor: "strategicRecoveryTrend",
    buildingLabel: "Optimise your PQ",
    description: (
      <>
        Strategic Recovery is the deliberate practice of returning to baseline after periods of
        sustained performance demand. It operates across two distinct mechanisms: psychological
        detachment (calming the mind), and parasympathetic activation (relaxing the body).
      </>
    ),
    buildingContent: [
      {
        left: { heading: "Plan extended recovery in advance.", text: "Schedule regular holidays and longer breaks in your calendar to ensure sustained restoration rather than relying on ad-hoc time off." },
        right: { heading: "Build micro recovery into each day.", text: "Incorporate short recovery breaks between periods of intense work — such as brief walks, breathing exercises, or mental resets — to prevent cumulative fatigue." }
      },
      {
        left: { heading: "Seek morning light exposure.", text: "Aim for sunlight exposure early in the day to support circadian rhythm alignment, optimise vitamin D synthesis, and stimulate key biological processes that influence energy and sleep." },
        right: { heading: "Deliberately wind down in the evening.", text: "Engage in relaxation practices before bedtime to promote psychological detachment from the day and prepare the nervous system for sleep." }
      },
      {
        left: { heading: "Prioritise consistent, restorative sleep.", text: "Aim for high-quality, restorative sleep on most nights, recognising sleep as the cornerstone of physical recovery, cognitive function, and emotional regulation." },
        right: { heading: "Detach psychologically from work.", text: "Switch off mentally, not just physically. Set a clear end to the workday (e.g. a walk, a ritual, a hard stop) and resist the pull to keep processing. Your brain can't recover while it's still running." }
      },
      {
        left: { heading: "Activate parasympathetic system", text: "Slow diaphragmatic breathing, light stretching, time in nature, or deliberate stillness directly lower heart rate, reduce cortisol, and shift the nervous system out of sympathetic overdrive." },
        right: { heading: "Practice sleep hygiene.", text: "Keep consistent sleep and wake times, even on weekends. A cool, dark room and a simple wind-down routine in the hour before bed make the difference between sleep that restores and sleep that doesn't." }
      }
    ]
  },
  {
    key: "mental_agility",
    title: "Mental Agility",
    icon: "PQ-mental-agility",
    anchor: "mentalAgilityTrend",
    buildingLabel: "Optimise your PQ",
    description: (
      <>
        Mental Agility is the capacity to move flexibly between demands, tasks, roles, and
        relationships while staying grounded and adaptable under pressure. It determines how
        much clarity and control you retain when conditions are hardest. Mental agility builds
        the capacity to fully engage in your work and personal life and comprises of emotional
        regulation, psychological flexibility and hardiness.
      </>
    ),
    buildingContent: [
      {
        left: { heading: "Understand the brain.", text: "Learn how the brain responds under pressure, including the hardwired tendency to weight threats more heavily than opportunities. Awareness of this pattern helps to choose a more deliberate response." },
        right: { heading: "Exercise to stimulate the brain.", text: "Physical activity discharges cortisol, resets the nervous system and shifts the brain out of threat mode. This is one of the fastest tools for restoring cognitive clarity and emotional balance." }
      },
      {
        left: { heading: "Stimulate brain growth and resilience.", text: "Increase levels of BDNF (brain-derived neurotrophic factor), a protein critical for neuron growth and cognitive resilience, through regular low-to-moderate intensity exercise with occasional bursts of higher-intensity interval training." },
        right: { heading: "Get clarity on personal identity", text: "Understand your values, strengths and what matters most so that pressure and change don't destabilise you. A clear sense of identity creates the psychological anchor that allows you to adapt without losing your sense of self." }
      },
      {
        left: { heading: "Protect sleep as a cognitive asset.", text: "Prioritise high-quality sleep to support memory consolidation, faster information processing, and improved emotional regulation — dramatically increasing the likelihood of waking in a better mental and emotional state." },
        right: { heading: "Optimise how you learn.", text: "Understand how you best acquire, retain, and recall information, and design systems, habits, and environments that support effective memory and learning." }
      },
      {
        left: { heading: "Develop orientation to challenge.", text: "Lean into challenge rather than avoiding it, and deliberately reframe difficulty as a primary driver of growth, adaptability, and long-term cognitive strength." },
        right: { heading: "Pursue deliberate cognitive challenge.", text: "Actively learn things that are genuinely difficult. Novelty and sustained challenge drive neuroplasticity; passive consumption does not." }
      }
    ]
  },
  {
    key: "satisfaction_with_life",
    title: "Satisfaction with Life",
    icon: "PQ-satisfaction-with-life",
    anchor: "satisfactionWithLifeTrend",
    description: (
      <>
        Satisfaction with Life sits outside the PQ Score by design. Where the other metrics
        measure specific physical and psychological capacities, this one reflects the overall
        quality and meaning of what it feels like to live with those capacities — capability is
        the engine, meaning is the fuel.
      </>
    )
  }
]

const toChartHistory = (history, valueKey) => (history || []).map(row => ({
  wellbeing_index: row[valueKey],
  completed_at_month: row.date
}))

const toTinyHistory = history => (history || []).map(row => {
  const entry = Object.entries(row).find(([k]) => k !== "date")
  return { wellbeing_index: entry ? entry[1] : 0 }
})

// BioAge graph cap
const BioAgeScoreCap = score => {
  if (score < -10) {
    return -10
  } if (score > 15) {
    return 15
  }
  return score
}

function LiveLifeV2BreakdownCard({ dashboard, baseUrl, enrolment }) {
  const [activeTab, setActiveTab] = useState("bio_age")
  const [openPanel, setOpenPanel] = useState("trend")

  const switchTab = tab => {
    setActiveTab(tab)
    setOpenPanel("trend")
  }

  const togglePanel = panel => setOpenPanel(prev => (prev === panel ? null : panel))

  if (!dashboard) return null

  const bioAge = dashboard.biological_age || {}
  const bioAgeCategories = bioAge.biological_age_categories || []
  const bioAgeTrendResults = toChartHistory(bioAge.biological_age_history, "age_change")

  const sectionData = SECTIONS.map(section => {
    const data = dashboard[section.key] || {}
    const percent = data[`${section.key}_percent`]
    const color = data[`${section.key}_color`]
    const history = data[`${section.key}_history`]
    const categories = data[`${section.key}_categories`] || []
    const historyValueKey = (history || []).length
      ? Object.keys(history[0]).find(k => k !== "date")
      : `${section.key}_percent`
    return {
      ...section, percent, color, categories, trendResults: toChartHistory(history, historyValueKey)
    }
  })

  return (
    <div className="dash-row card lls" id="ditem">
      <div className="card-body">
        <h3>Your PQ Index Breakdown</h3>

        {/* Tab nav */}
        <ul className="nav nav-tabs breakdown-tabs" role="tablist">
          <li className="nav-item" role="presentation">
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === "bio_age"}
              className={`nav-link breakdown-tab${activeTab === "bio_age" ? " active" : ""}`}
              onClick={() => switchTab("bio_age")}>
              <span className="breakdown-tab-title">Biological Age</span>
              <strong className="breakdown-tab-score mt-1" style={{ color: bioAge.biological_age_color ? `#${bioAge.biological_age_color}` : undefined }}>{ageChangeScoreDisplay(bioAge.age_change)}</strong>
            </button>
          </li>
          {sectionData.map(section => (
            <li className="nav-item" key={section.key} role="presentation">
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === section.key}
                className={`nav-link breakdown-tab${activeTab === section.key ? " active" : ""}`}
                onClick={() => switchTab(section.key)}>
                <span className="breakdown-tab-title">{section.title}</span>
                <strong className="breakdown-tab-score mt-1" style={{ color: section.color ? `#${section.color}` : undefined }}>{section.percent != null ? `${section.percent}%` : "—"}</strong>
              </button>
            </li>
          ))}
        </ul>

        {/* Tab content */}
        <div className="tab-content">
          <div className="tab-pane active breakdown-tab-panel breakdown-wrapper-inner pt-5">
            {activeTab === "bio_age" && (
              <>
                <div className="breakdown-section">
                  <div>
                    <div className="d-flex align-items-center mb-3 breakdown-section-heading">
                      <img src={getAssetPath("PQ-biological-age")} width={64} height={64} />
                      <h4 className="mb-0 ml-3">Biological Age</h4>
                    </div>
                    <small>
                      Biological Age is a measure of how your body is functioning right now. It draws
                      on physical fitness, metabolic health, and lifestyle factors linked to performance,
                      longevity and chronic disease risk. Known health and longevity risk factors push it
                      higher. Strong results push biological age below chronological age. The target is to
                      be at least five years younger.
                    </small>
                    <div className="d-flex align-items-center mt-3" style={{ gap: "0.75rem" }}>
                      <SectionRetakeButton baseUrl={baseUrl} section="biological_age" sectionLabel="Biological Age" enrolment={enrolment} />
                    </div>
                  </div>
                  <div className="col-md-5 p-0">
                    <IndexGaugeSmallLlsBioAge
                      barIndex={bioAge.biological_age_percent}
                      color={bioAge.biological_age_color}
                      index={ageChangeScoreDisplay(bioAge.age_change)} />
                  </div>
                </div>
                <div className="data-table mb-5">
                  <dl>
                    <dt><strong>Biological Age</strong></dt>
                    <dd><strong style={{ color: bioAge.biological_age_color ? `#${bioAge.biological_age_color}` : undefined }}>{bioAge.biological_age}</strong></dd>
                  </dl>
                  <dl>
                    <dt>Chronological Age</dt>
                    <dd>{bioAge.age}</dd>
                  </dl>
                  <dl>
                    <dt>Difference</dt>
                    <dd><strong style={{ color: bioAge.age_change_color ? `#${bioAge.age_change_color}` : undefined }}>{ageChangeScoreDisplay(bioAge.age_change)}</strong></dd>
                  </dl>
                </div>
                <div className="biological-age">
                  <div className="ditem-grid-container livelifescore-grid">
                    <div id="ditem-grid" />
                    <div className="ditem-questions">
                      {!!bioAgeCategories.length && bioAgeCategories.map(category => (
                        <div className="row ditem-questions-item no-gutters" key={category?.category_name}>
                          <div className="col-lg-6 dimension-question">
                            <div className="d-flex justify-content-between mr-4">
                              <div className="mr-4 d-inline-flex align-items-center">
                                <h6 className="mb-0">{category?.category_name}</h6>
                                <img
                                  data-tooltip-id={`sectionDescriptor${category?.category_name}`}
                                  className="ml-2"
                                  src={getAssetPath("icn-info")} />
                                <Tooltip className="tooltip" id={`sectionDescriptor${category?.category_name}`} place="top">
                                  <span>{category?.category_tooltip}</span>
                                </Tooltip>
                              </div>
                              <div className="d-flex align-items-center">
                                <strong className="mr-2">{ageChangeScoreDisplay(category?.category_score)}</strong>
                              </div>
                            </div>
                          </div>
                          <div className="col-lg-6 dimension-graph">
                            <div className="progress" style={{ height: "1px", display: "grid" }}>
                              {category?.category_score >= 0 ? (
                                <div
                                  className="progress-bar"
                                  role="progressbar"
                                  aria-label={`${category?.category_name} age increment`}
                                  style={{ width: `${Math.round((100 / 15) * BioAgeScoreCap(category?.category_score))}%` }} />
                              ) : (
                                <div
                                  className="progress-bar"
                                  role="progressbar"
                                  aria-label={`${category?.category_name} age increment`}
                                  style={{ width: `calc(calc(100% - 33.3%) * ${Math.abs(BioAgeScoreCap(category.category_score)) / 10})`, transform: "translate(-100%, 10px) rotate(180deg)" }} />
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="colour-bar">
                    <div className="bar-item bg-green" />
                    <div className="bar-item bg-green" />
                    <div className="bar-item bg-red" />
                    <div className="bar-item bg-red" />
                    <div className="bar-item bg-red" />
                  </div>
                  <ul className="nav nav-fill ditem-weight">
                    <li className="nav-item">-10</li>
                    <li className="nav-item">-5</li>
                    <li className="nav-item">0</li>
                    <li className="nav-item">+5</li>
                    <li className="nav-item">+10</li>
                    <li className="nav-item">+15</li>
                  </ul>
                </div>
                <div className="d-flex align-items-center mt-4" style={{ gap: "0.75rem" }}>
                  <button type="button" className={`btn btn-outline-secondary btn-sm${openPanel === "trend" ? " active" : ""}`} onClick={() => togglePanel("trend")}>See your trend</button>
                </div>
                {openPanel === "trend" && (
                  <div className="my-5">
                    <h6>Biological Age Trend</h6>
                    <div className="wellbeingTrend">
                      <div className="colour-bar">
                        <div className="bar-item bg-green" />
                        <div className="bar-item bg-gold" />
                        <div className="bar-item bg-red" />
                        <div className="bar-item bg-red" />
                        <div className="bar-item bg-red" />
                      </div>
                      <WellbeingIndexChartLlsBioAge results={bioAgeTrendResults} />
                    </div>
                  </div>
                )}
              </>
            )}

            {sectionData.map(section => activeTab === section.key && (
              <React.Fragment key={section.key}>
                <div className="breakdown-section">
                  <div>
                    <div className="d-flex align-items-center mb-3 breakdown-section-heading">
                      <img src={getAssetPath(section.icon)} width={64} height={64} />
                      <h4 className="mb-0 ml-3">{section.title}</h4>
                    </div>
                    {section.description && <small>{section.description}</small>}
                    <div className="d-flex align-items-center mt-3" style={{ gap: "0.75rem" }}>
                      <SectionRetakeButton baseUrl={baseUrl} section={section.key} sectionLabel={section.title} enrolment={enrolment} />
                    </div>
                  </div>
                  <div className="col-md-5 p-0">
                    <IndexGaugeSmall
                      color={section.color}
                      index={section.percent}
                      showPercent
                      category={section.percent != null ? pqIndexBandName(section.percent) : undefined} />
                  </div>
                </div>
                <div className="mental-gauge">
                  <div className="ditem-grid-container global-wellbeing-grid">
                    <div id="ditem-grid" />
                    <div className="ditem-questions">
                      {!!section.categories.length && section.categories.map(category => (
                        <div className="row ditem-questions-item no-gutters" key={category?.category_name}>
                          <div className="col-lg-6 dimension-question">
                            <div className="d-flex justify-content-between mr-4">
                              <div className="mr-4 d-inline-flex align-items-center">
                                <h6 className="mb-0">{category?.category_name}</h6>
                                <img
                                  data-tooltip-id={`sectionDescriptor${section.key}-${category?.category_name}`}
                                  className="ml-2"
                                  src={getAssetPath("icn-info")} />
                                <Tooltip className="tooltip" id={`sectionDescriptor${section.key}-${category?.category_name}`} place="top">
                                  <span>{category?.category_tooltip}</span>
                                </Tooltip>
                              </div>
                              <div className="d-flex align-items-center">
                                <div className="sparkline-wrapper">
                                  {(category?.score_history?.length || 0) > 1 && (
                                    <WellbeingIndexChartTiny results={toTinyHistory(category?.score_history)} />
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="col-lg-6 dimension-graph">
                            <div className="progress" style={{ height: "1px", display: "grid" }}>
                              <div
                                className="progress-bar"
                                role="progressbar"
                                aria-label={`${category?.category_name} score`}
                                style={{ width: `${category?.category_score}%` }} />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="colour-bar">
                    <div className="bar-item bg-red" />
                    <div className="bar-item bg-red" />
                    <div className="bar-item bg-orange" />
                    <div className="bar-item bg-gold" />
                    <div className="bar-item bg-green" />
                  </div>
                  <ul className="nav nav-fill ditem-weight">
                    <li className="nav-item">0</li>
                    <li className="nav-item">25</li>
                    <li className="nav-item">50</li>
                    <li className="nav-item">75</li>
                    <li className="nav-item">100</li>
                  </ul>
                </div>
                <div className="d-flex align-items-center mt-5" style={{ gap: "0.75rem" }}>
                  <button type="button" className={`btn btn-outline-secondary btn-sm${openPanel === "trend" ? " active" : ""}`} onClick={() => togglePanel("trend")}>See your trend</button>
                  {section.buildingLabel && (
                    <button type="button" className={`btn btn-outline-secondary btn-sm${openPanel === "building" ? " active" : ""}`} onClick={() => togglePanel("building")}>{section.buildingLabel}</button>
                  )}
                </div>
                {section.buildingContent?.length > 0 && openPanel === "building" && (
                  <div className="mt-5">
                    <h4 className="mb-3">{section.buildingLabel}</h4>
                    <table className="table table-bordered building-table">
                      <tbody>
                        {section.buildingContent.map((row, i) => (
                          // eslint-disable-next-line react/no-array-index-key
                          <tr key={i}>
                            <td>
                              <strong className="building-table-heading">{row.left.heading}</strong>
                              <p className="mb-0 mt-1">{row.left.text}</p>
                            </td>
                            <td>
                              <strong className="building-table-heading">{row.right.heading}</strong>
                              <p className="mb-0 mt-1">{row.right.text}</p>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {openPanel === "trend" && (
                  <div className="my-5">
                    <h6>{`${section.title} Trend`}</h6>
                    <WellbeingIndexChart
                      results={section.trendResults}
                      xAxisLabel="Assessment date"
                      yAxisLabel="Score" />
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

export default LiveLifeV2BreakdownCard
